/**
 * Two-stage safety controller: NORMAL -> SLOWDOWN -> EMERGENCY_STOP.
 * Two hazard channels: dynamic TTC vs the pedestrian, and a static
 * free-distance braking cap along the intended travel heading.
 * Produces a speed cap the robot controller must respect. Deterministic.
 */

import { clamp } from "../core/vec";
import {
  SAFETY_LOOKAHEAD,
  SAFETY_MARGIN,
  STATIC_CLEAR_DIST,
  STATIC_MARGIN,
  STATIC_STOP_DIST,
  TTC_SLOWDOWN_CLEAR,
  TTC_STOP,
} from "./constants";
import type { EffectiveProfile } from "./profile";
import type { RobotState, SafetyHazard, SafetyState, WorldDef } from "./types";
import { bodyCircles, freeDistance, type RobotShape } from "./world";

/** Per-level safety semantics (lab levels keep the ADR-017 behaviour exactly). */
export interface SafetyOptions {
  /** Ignore hazards entirely behind the body (mission). */
  ignoreBehind: boolean;
  /** Inside-the-margin-but-not-closing is no collision course (mission). */
  insideNotClosing: boolean;
  /** Emergency stop clears when TTC exceeds this multiple of TTC_STOP. */
  ttcClearFactor: number;
  /** Static channel also checks the robot's ACTUAL heading (the body keeps
   * travelling along it while it turns toward the intended one). */
  sweepActual: boolean;
  /**
   * Whether the governor may brake BEFORE contact.
   *
   * With this off the robot behaves like a vehicle with no driver assistance:
   * nothing slows it down for a hazard it has not touched yet, and it stops
   * because it hit something, not because it might. The TTC and free-distance
   * channels are still computed and still reported — they are the raw material
   * for the time-to-collision metric — they simply no longer command the
   * throttle. Contact handling in the simulation does the stopping instead.
   */
  preemptive: boolean;
}

export const LAB_SAFETY: SafetyOptions = { ignoreBehind: false, insideNotClosing: false, ttcClearFactor: 1.5, sweepActual: false, preemptive: true };
export const MISSION_SAFETY: SafetyOptions = { ignoreBehind: true, insideNotClosing: true, ttcClearFactor: 2.0, sweepActual: true, preemptive: true };
/** Mission semantics with the assistance removed: real-physics contact only. */
export const REAL_SAFETY: SafetyOptions = { ...MISSION_SAFETY, preemptive: false };

export interface SafetyResult {
  state: SafetyState;
  /** Max speed the controller may command (m/s). */
  speedCap: number;
  /** TTC to the pedestrian (s), Infinity when not closing. */
  ttc: number;
  /** Free distance along the intended travel heading (m). */
  freeDist: number;
  /** True when the binding constraint is static geometry, not the pedestrian. */
  staticBound: boolean;
}

/** Time to collision of two circles from relative position/velocity. */
export function timeToCollision(
  relX: number,
  relZ: number,
  relVx: number,
  relVz: number,
  radius: number,
  /** Mission semantics (ADR-022): already inside the safety radius counts as
   * a collision course only while still closing. The lab default keeps the
   * original "inside = 0" so lab trajectories are unchanged. */
  insideNotClosing = false,
): number {
  const c = relX * relX + relZ * relZ - radius * radius;
  const b = relX * relVx + relZ * relVz;
  if (c <= 0) return insideNotClosing && b >= 0 ? Infinity : 0;
  const a = relVx * relVx + relVz * relVz;
  if (a < 1e-12) return Infinity;
  if (b >= 0) return Infinity; // not closing
  const disc = b * b - a * c;
  if (disc < 0) return Infinity;
  return (-b - Math.sqrt(disc)) / a;
}

export function evaluateSafety(
  world: WorldDef,
  robot: RobotState,
  shape: RobotShape,
  hazards: readonly SafetyHazard[],
  profile: EffectiveProfile,
  prevState: SafetyState,
  /** Heading the robot intends to travel along this step (post-assist). */
  travelHeading: number,
  opts: SafetyOptions = LAB_SAFETY,
): SafetyResult {
  // --- Dynamic hazards: TTC vs every active agent, per body circle (matches
  // the two-circle collision model; an enclosing circle would falsely flag
  // clean side-by-side passes as collision courses). Uses each hazard's
  // sway-free intent velocity: gait sway reverses within a quarter period, so
  // extrapolating it as constant would fire spurious emergency stops.
  let ttc = Infinity;
  const fx = Math.sin(robot.heading);
  const fz = Math.cos(robot.heading);
  const rvxBase = -robot.speed * fx;
  const rvzBase = -robot.speed * fz;
  for (const hz of hazards) {
    // A hazard entirely BEHIND the body cannot be hit by driving forward;
    // braking for something closing from behind would only invite the hit
    // (and agents carry their own courtesy guard). Ignore it here.
    const behind = (hz.x - robot.x) * fx + (hz.z - robot.z) * fz < -(shape.effR + hz.radius);
    if (opts.ignoreBehind && behind) continue;
    const rvx = hz.safetyVx + rvxBase;
    const rvz = hz.safetyVz + rvzBase;
    for (const c of bodyCircles(robot, shape)) {
      ttc = Math.min(
        ttc,
        timeToCollision(
          hz.x - c.x,
          hz.z - c.z,
          rvx,
          rvz,
          shape.r + hz.radius + SAFETY_MARGIN,
          opts.insideNotClosing,
        ),
      );
    }
  }

  const ttcCap =
    ttc >= profile.safety_ttc_s
      ? profile.max_speed_mps
      : profile.cruise_mps *
        clamp((ttc - TTC_STOP) / (profile.safety_ttc_s - TTC_STOP), 0, 1);

  // --- Static hazard: how far can we roll along the intended heading? ---
  let freeDist = freeDistance(world, robot, robot.heading, travelHeading, shape, hazards);
  // Along the heading the body is ACTUALLY on: obstacles and agents bound the
  // stop decision like the intended heading does (they do not slide); curbs
  // only cap the speed (the wall clamp slides the body along them).
  const freeIntended = freeDist;
  let capDist = freeDist;
  if (opts.sweepActual && Math.abs(travelHeading - robot.heading) > 1e-6) {
    freeDist = Math.min(
      freeDist,
      freeDistance(world, robot, robot.heading, robot.heading, shape, hazards, true),
    );
    capDist = Math.min(freeDist, freeDistance(world, robot, robot.heading, robot.heading, shape, []));
  }
  const braking = Math.max(0, capDist - STATIC_MARGIN);
  let staticCap =
    capDist >= SAFETY_LOOKAHEAD
      ? profile.max_speed_mps
      : Math.sqrt(2 * profile.max_decel_mps2 * braking);
  // A STATIONARY robot whose nose touches a wall but whose commanded heading
  // is clear may creep: the actual-heading sweep exists because a MOVING body
  // keeps travelling its old heading while it turns, and at standstill that
  // rationale vanishes. Without this, a wedged robot whose steering error sat
  // inside the wheel deadzone stayed wedged forever.
  if (opts.sweepActual && robot.speed < 0.5 && freeIntended > 1.5) {
    staticCap = Math.max(staticCap, 0.6);
  }

  const speedCapRaw = Math.min(ttcCap, staticCap);
  const staticBound = staticCap <= ttcCap;

  // Real physics: report what the sensors see, command nothing. Returning
  // NORMAL rather than a suppressed SLOWDOWN keeps the telemetry honest —
  // there was no slowdown, so no slowdown is logged.
  if (!opts.preemptive) {
    return { state: "NORMAL", speedCap: profile.max_speed_mps, ttc, freeDist, staticBound };
  }

  // --- State machine with hysteresis on the emergency-stop boundary. ---
  let state: SafetyState;
  const wantsStop = ttc < TTC_STOP || freeDist < STATIC_STOP_DIST;
  if (prevState === "EMERGENCY_STOP") {
    const cleared = ttc > opts.ttcClearFactor * TTC_STOP && freeDist > STATIC_CLEAR_DIST;
    state = cleared ? "SLOWDOWN" : "EMERGENCY_STOP";
  } else if (wantsStop) {
    state = "EMERGENCY_STOP";
  } else if (speedCapRaw < profile.cruise_mps - 1e-6) {
    state = "SLOWDOWN";
  } else if (prevState === "SLOWDOWN" && ttc < TTC_SLOWDOWN_CLEAR * profile.safety_ttc_s) {
    // Debounce the SLOWDOWN->NORMAL edge (state only; the cap is unchanged),
    // so a TTC hovering at the threshold counts as ONE slowdown, not twenty.
    state = "SLOWDOWN";
  } else {
    state = "NORMAL";
  }

  const speedCap = state === "EMERGENCY_STOP" ? 0 : speedCapRaw;
  return { state, speedCap, ttc, freeDist, staticBound };
}
